import React, { useState, useEffect, useCallback } from 'react';
import { useConnection, useWallet } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import { LAMPORTS_PER_SOL } from '@solana/web3.js';
import { Program, AnchorProvider } from '@coral-xyz/anchor';
import GameLobby from './components/GameLobby';
import PokerTable from './components/PokerTable';
import { IDL } from './idl';

const PP_TOKEN_CA = 'DrnF17MbiKXu7gVyfL13UydVvhFTSM7DDWN3Ui8npump';
const PROGRAM_ID_DISPLAY = 'CpHzGi7W...SBSAh';
const PLATFORM_FEE = 2;

function App() {
  const { connection } = useConnection();
  const wallet = useWallet();
  const [balance, setBalance] = useState(0);
  const [currentGame, setCurrentGame] = useState(null);
  const [program, setProgram] = useState(null);

  // Fake live stats
  const [totalBurned, setTotalBurned] = useState(0);
  const [gamesToday, setGamesToday] = useState(0);
  const [totalWagered, setTotalWagered] = useState(0);
  const [activePlayers, setActivePlayers] = useState(0);



  // Initialize program
  useEffect(() => {
    if (wallet.publicKey && wallet.signTransaction) {
      const provider = new AnchorProvider(connection, wallet, { commitment: 'confirmed' });
      const prog = new Program(IDL, provider);
      setProgram(prog);
    }
  }, [connection, wallet]);

  // Fetch balance
  const fetchBalance = useCallback(async () => {
    if (!wallet.publicKey) return;
    try {
      const bal = await connection.getBalance(wallet.publicKey);
      setBalance(bal / LAMPORTS_PER_SOL);
    } catch (e) { console.error('Balance fetch error:', e); }
  }, [connection, wallet.publicKey]);

  useEffect(() => {
    fetchBalance();
    const interval = setInterval(fetchBalance, 5000);
    return () => clearInterval(interval);
  }, [fetchBalance]);

  const handleJoinGame = (game) => setCurrentGame(game);
  const handleLeaveGame = () => { setCurrentGame(null); fetchBalance(); };

  // ───── LANDING PAGE (not connected) ─────
  if (!wallet.publicKey) {
    return (
      <div style={s.app}>
        {/* Burn Ticker Banner */}
        <BurnTicker totalBurned={totalBurned} />

        {/* Nav */}
        <nav style={s.nav}>
          <div style={s.navLeft}>
            <span style={s.logo}>🎰</span>
            <span style={s.logoText}>Private Poker</span>
          </div>
          <WalletMultiButton />
        </nav>

        {/* Hero */}
        <section style={s.hero}>
          <div style={s.heroSlot}>🎰</div>
          <h1 style={s.heroTitle}>
            <span style={s.heroGreen}>Decentralized</span>
            <br />
            <span style={s.heroGold}>Privacy Poker</span>
          </h1>
          <p style={s.heroSub}>
            Play Texas Hold'em with <span style={s.heroHighlight}>provably fair</span> outcomes on the blockchain.
          </p>
          <p style={s.heroDesc}>
            Your cards are encrypted in Intel TDX. Nobody—not even us—can see your hand until showdown.
          </p>
        </section>

        {/* Feature Cards */}
        <section style={s.features}>
          {[
            { icon: '🔐', title: '100% Private', desc: 'Cards encrypted in TEE' },
            { icon: '⚡', title: 'Instant Gameplay', desc: '5s MagicBlock ER blocks' },
            { icon: '💎', title: 'Real Stakes', desc: 'SOL winnings on-chain' },
            { icon: '🏆', title: 'Fair & Auditable', desc: 'All moves on Solana' },
          ].map((f, i) => (
            <div key={i} style={s.featureCard}>
              <span style={s.featureIcon}>{f.icon}</span>
              <h3 style={s.featureTitle}>{f.title}</h3>
              <p style={s.featureDesc}>{f.desc}</p>
            </div>
          ))}
        </section>

        {/* 🔥 $PP Burn Mechanism Section */}
        <section style={s.burnSection}>
          <div style={s.burnCard}>
            <div style={s.burnCardHeader}>
              <span style={s.burnFireBig}>🔥</span>
              <h2 style={s.burnCardTitle}>Deflationary Burn Engine</h2>
            </div>
            <p style={s.burnCardDesc}>
              <strong style={{ color: '#ffcc02' }}>{PLATFORM_FEE}%</strong> of every pot is used to buy{' '}
              <strong style={{ color: '#ffcc02' }}>$PP</strong> tokens from the open market and{' '}
              <strong style={{ color: '#ff4444' }}>permanently burn</strong> them. Every hand makes $PP more scarce.
            </p>
            <div style={s.burnStats}>
              <div style={s.burnStatBox}>
                <span style={s.burnStatNum}>{totalBurned.toLocaleString()}</span>
                <span style={s.burnStatLabel}>$PP Burned Forever</span>
              </div>
              <div style={s.burnStatBox}>
                <span style={s.burnStatNum}>{PLATFORM_FEE}%</span>
                <span style={s.burnStatLabel}>Of Every Pot</span>
              </div>
            </div>
            <div style={s.burnTokenCA}>
              <span style={s.burnCALabel}>$PP Contract Address</span>
              <a
                href={`https://solscan.io/token/${PP_TOKEN_CA}`}
                target="_blank"
                rel="noopener noreferrer"
                style={s.burnCALink}
              >
                {PP_TOKEN_CA}
              </a>
            </div>
            <div style={s.burnFlow}>
              <div style={s.burnFlowStep}>🃏 You Play</div>
              <span style={s.burnArrow}>→</span>
              <div style={s.burnFlowStep}>💰 2% Fee</div>
              <span style={s.burnArrow}>→</span>
              <div style={s.burnFlowStep}>🛒 Buy $PP</div>
              <span style={s.burnArrow}>→</span>
              <div style={{...s.burnFlowStep, ...s.burnFlowBurn}}>🔥 Burn</div>
            </div>
          </div>
        </section>

        {/* Live Stats */}
        <section style={s.statsSection}>
          <div style={s.statsCard}>
            <div style={s.stat}>
              <span style={s.statNum}>{gamesToday}</span>
              <span style={s.statLabel}>Games Today</span>
            </div>
            <div style={s.stat}>
              <span style={s.statNum}>{totalWagered.toLocaleString()}</span>
              <span style={s.statLabel}>Total SOL Wagered</span>
            </div>
            <div style={s.stat}>
              <span style={s.statNum}>{activePlayers}</span>
              <span style={s.statLabel}>Active Players</span>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section style={s.ctaSection}>
          <div style={s.ctaCard}>
            <div>
              <span style={s.ctaSmall}>GET STARTED</span>
              <span style={s.ctaText}>Connect your wallet</span>
            </div>
            <WalletMultiButton />
          </div>
          <p style={s.ctaFine}>Connect any Solana wallet • No KYC • Instant play • Secure on-chain</p>
        </section>

        {/* Footer */}
        <footer style={s.footer}>
          <div style={s.footerBadges}>
            <span style={s.badge}>⛓️ Solana</span>
            <span style={s.badge}>✨ MagicBlock</span>
            <span style={s.badge}>🔒 Intel TDX</span>
            <span style={{...s.badge, ...s.badgeBurn}}>🔥 $PP Burn</span>
          </div>
          <p style={s.footerText}>
            Powered by MagicBlock Ephemeral Rollups • Deployed on Solana Devnet
          </p>
          <p style={s.footerProgram}>Program ID: {PROGRAM_ID_DISPLAY}</p>
          <p style={s.footerToken}>$PP Token: {PP_TOKEN_CA.slice(0, 12)}...{PP_TOKEN_CA.slice(-8)}</p>
        </footer>
      </div>
    );
  }

  // ───── CONNECTED: GAME LOBBY / TABLE ─────
  return (
    <div style={s.app}>
      <BurnTicker totalBurned={totalBurned} />

      <nav style={s.nav}>
        <div style={s.navLeft}>
          <span style={s.logo}>🎰</span>
          <span style={s.logoText}>Private Poker</span>
        </div>
        <div style={s.navRight}>
          {wallet.publicKey && (
            <span style={s.balanceBadge}>{balance.toFixed(4)} SOL</span>
          )}
          <WalletMultiButton />
        </div>
      </nav>

      <main style={s.main}>
        {!currentGame ? (
          <GameLobby
            program={program}
            wallet={wallet}
            connection={connection}
            onJoinGame={handleJoinGame}
            onRefresh={fetchBalance}
          />
        ) : (
          <PokerTable
            program={program}
            wallet={wallet}
            connection={connection}
            game={currentGame}
            onLeave={handleLeaveGame}
            onRefresh={fetchBalance}
          />
        )}
      </main>
    </div>
  );
}

// ───── BURN TICKER COMPONENT ─────
function BurnTicker({ totalBurned }) {
  return (
    <div style={s.burnTicker}>
      <div style={s.burnTickerInner}>
        <span style={s.tickerFire}>🔥</span>
        <span style={s.tickerLabel}>TOTAL $PP BURNED:</span>
        <span style={s.tickerValue}>{totalBurned.toLocaleString()}</span>
        <span style={s.tickerDivider}>|</span>
        <span style={s.tickerLabel}>PLATFORM FEE:</span>
        <span style={s.tickerFee}>2% → Buy & Burn</span>
        <span style={s.tickerDivider}>|</span>
        <a
          href={`https://solscan.io/token/${PP_TOKEN_CA}`}
          target="_blank"
          rel="noopener noreferrer"
          style={s.tickerCA}
        >
          $PP: {PP_TOKEN_CA.slice(0, 6)}...{PP_TOKEN_CA.slice(-4)}
        </a>
      </div>
    </div>
  );
}

// ───── ALL STYLES ─────
const s = {
  app: {
    minHeight: '100vh',
    background: 'linear-gradient(180deg, #0a0e1a 0%, #111827 40%, #0f172a 100%)',
    color: '#e2e8f0',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },

  // Burn Ticker
  burnTicker: {
    background: 'linear-gradient(90deg, rgba(255,69,0,0.12) 0%, rgba(255,140,0,0.08) 50%, rgba(255,69,0,0.12) 100%)',
    borderBottom: '1px solid rgba(255,140,0,0.25)',
    padding: '0.45rem 1.5rem',
  },
  burnTickerInner: {
    display: 'flex', justifyContent: 'center', alignItems: 'center',
    gap: '0.7rem', flexWrap: 'wrap', fontSize: '0.78rem',
  },
  tickerFire: { fontSize: '0.95rem' },
  tickerLabel: { color: '#ff9800', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.68rem' },
  tickerValue: { color: '#ffcc02', fontWeight: '800', fontFamily: 'monospace', fontSize: '0.88rem' },
  tickerFee: { color: '#ff6b35', fontWeight: '700', fontSize: '0.78rem' },
  tickerDivider: { color: 'rgba(255,140,0,0.3)', fontWeight: '300' },
  tickerCA: { color: '#ffcc02', textDecoration: 'none', fontFamily: 'monospace', fontSize: '0.72rem', borderBottom: '1px dashed rgba(255,204,2,0.3)' },

  // Nav
  nav: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '1rem 2rem',
    background: 'rgba(0,0,0,0.25)',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    backdropFilter: 'blur(10px)',
  },
  navLeft: { display: 'flex', alignItems: 'center', gap: '0.6rem' },
  navRight: { display: 'flex', alignItems: 'center', gap: '1rem' },
  logo: { fontSize: '1.8rem' },
  logoText: { fontSize: '1.2rem', fontWeight: '700', color: '#fff' },
  balanceBadge: {
    background: 'rgba(76,175,80,0.15)', padding: '0.45rem 1rem',
    borderRadius: '8px', color: '#4caf50', fontWeight: '600',
    fontSize: '0.9rem', border: '1px solid rgba(76,175,80,0.2)',
  },

  // Hero
  hero: { textAlign: 'center', padding: '4rem 2rem 2rem' },
  heroSlot: { fontSize: '5rem', marginBottom: '1rem', filter: 'drop-shadow(0 0 20px rgba(255,200,0,0.3))' },
  heroTitle: { fontSize: '3.2rem', fontWeight: '800', lineHeight: '1.15', margin: '0 0 1.2rem' },
  heroGreen: { color: '#4ade80', fontStyle: 'italic' },
  heroGold: { color: '#fbbf24', fontStyle: 'italic' },
  heroSub: { fontSize: '1.15rem', color: '#94a3b8', marginBottom: '0.5rem' },
  heroHighlight: { color: '#4ade80', fontWeight: '600' },
  heroDesc: { fontSize: '0.95rem', color: '#64748b', maxWidth: '550px', margin: '0 auto' },

  // Features
  features: {
    display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem',
    maxWidth: '900px', margin: '2.5rem auto', padding: '0 2rem',
  },
  featureCard: {
    background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '12px', padding: '1.5rem 1.2rem',
    transition: 'border-color 0.2s',
  },
  featureIcon: { fontSize: '1.8rem', display: 'block', marginBottom: '0.8rem' },
  featureTitle: { fontSize: '1rem', fontWeight: '700', color: '#f1f5f9', margin: '0 0 0.3rem' },
  featureDesc: { fontSize: '0.82rem', color: '#64748b', margin: 0 },

  // 🔥 Burn Mechanism Section
  burnSection: { maxWidth: '700px', margin: '2rem auto', padding: '0 2rem' },
  burnCard: {
    background: 'linear-gradient(135deg, rgba(255,69,0,0.08) 0%, rgba(255,140,0,0.04) 100%)',
    border: '1px solid rgba(255,140,0,0.2)',
    borderRadius: '16px', padding: '2rem', textAlign: 'center',
  },
  burnCardHeader: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.6rem', marginBottom: '1rem' },
  burnFireBig: { fontSize: '2rem' },
  burnCardTitle: { fontSize: '1.4rem', fontWeight: '800', color: '#ff9800', margin: 0 },
  burnCardDesc: { color: '#94a3b8', fontSize: '0.95rem', lineHeight: '1.6', marginBottom: '1.5rem' },
  burnStats: { display: 'flex', justifyContent: 'center', gap: '2rem', marginBottom: '1.5rem' },
  burnStatBox: {
    background: 'rgba(0,0,0,0.3)', padding: '1rem 1.5rem', borderRadius: '10px',
    display: 'flex', flexDirection: 'column', gap: '0.3rem',
    border: '1px solid rgba(255,140,0,0.15)',
  },
  burnStatNum: { fontSize: '1.6rem', fontWeight: '800', color: '#ffcc02', fontFamily: 'monospace' },
  burnStatLabel: { fontSize: '0.7rem', color: '#ff9800', textTransform: 'uppercase', fontWeight: '600', letterSpacing: '0.5px' },
  burnTokenCA: {
    background: 'rgba(0,0,0,0.4)', padding: '0.8rem 1.2rem', borderRadius: '8px',
    marginBottom: '1.5rem',
  },
  burnCALabel: { display: 'block', fontSize: '0.65rem', color: '#ff9800', textTransform: 'uppercase', fontWeight: '700', letterSpacing: '1px', marginBottom: '0.3rem' },
  burnCALink: { color: '#ffcc02', fontSize: '0.78rem', fontFamily: 'monospace', textDecoration: 'none', wordBreak: 'break-all', borderBottom: '1px dashed rgba(255,204,2,0.3)' },
  burnFlow: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
    flexWrap: 'wrap',
  },
  burnFlowStep: {
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
    padding: '0.5rem 0.9rem', borderRadius: '8px', fontSize: '0.8rem', fontWeight: '600',
  },
  burnFlowBurn: { background: 'rgba(255,69,0,0.15)', borderColor: 'rgba(255,69,0,0.3)', color: '#ff6b35' },
  burnArrow: { color: '#ff9800', fontWeight: '700', fontSize: '1.1rem' },

  // Stats
  statsSection: { maxWidth: '650px', margin: '2rem auto', padding: '0 2rem' },
  statsCard: {
    background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '14px', padding: '1.5rem 2rem',
    display: 'flex', justifyContent: 'space-around',
  },
  stat: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.3rem' },
  statNum: { fontSize: '1.8rem', fontWeight: '800', color: '#fbbf24', fontFamily: 'monospace' },
  statLabel: { fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: '600' },

  // CTA
  ctaSection: { textAlign: 'center', padding: '2rem 2rem 1rem' },
  ctaCard: {
    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '14px', padding: '1.2rem 2rem',
    display: 'inline-flex', alignItems: 'center', gap: '1.5rem',
  },
  ctaSmall: { display: 'block', fontSize: '0.65rem', color: '#64748b', textTransform: 'uppercase', fontWeight: '700', letterSpacing: '1px', textAlign: 'left' },
  ctaText: { display: 'block', fontSize: '1.05rem', fontWeight: '600', color: '#f1f5f9', textAlign: 'left' },
  ctaFine: { fontSize: '0.75rem', color: '#475569', marginTop: '0.8rem' },

  // Footer
  footer: { textAlign: 'center', padding: '2.5rem 2rem', borderTop: '1px solid rgba(255,255,255,0.05)', marginTop: '2rem' },
  footerBadges: { display: 'flex', justifyContent: 'center', gap: '0.6rem', marginBottom: '1rem', flexWrap: 'wrap' },
  badge: {
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)',
    padding: '0.4rem 0.8rem', borderRadius: '8px', fontSize: '0.78rem', color: '#94a3b8',
  },
  badgeBurn: { borderColor: 'rgba(255,140,0,0.3)', color: '#ff9800', background: 'rgba(255,69,0,0.1)' },
  footerText: { fontSize: '0.8rem', color: '#475569', margin: '0 0 0.3rem' },
  footerProgram: { fontSize: '0.72rem', color: '#374151', fontFamily: 'monospace' },
  footerToken: { fontSize: '0.72rem', color: '#6b4c00', fontFamily: 'monospace', marginTop: '0.2rem' },

  // Main (connected)
  main: { padding: '2rem', maxWidth: '1200px', margin: '0 auto' },
};

export default App;
